import { api } from './authService';
import { API_BASE_URL } from '@config/api';

export interface Tip {
  id: string;
  category_id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  author_clinic_id?: string;
  author_user_id?: string;
  is_master_content: boolean;
  status: 'draft' | 'pending' | 'approved' | 'rejected' | 'archived';
  reviewed_by?: string;
  reviewed_at?: Date;
  review_comments?: string;
  target_roles?: string[];
  target_animal_types?: string[];
  target_regions?: string[];
  is_featured: boolean;
  is_public: boolean;
  cover_image?: string;
  images?: string[];
  tags?: string[];
  meta_title?: string;
  meta_description?: string;
  metadata?: any;
  views_count: number;
  likes_count: number;
  shares_count: number;
  published_at?: Date;
  archived_at?: Date;
  created_at: Date;
  updated_at: Date;
  category_name?: string;
  category_slug?: string;
}

export interface TipCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  color?: string;
  order: number;
  is_active: boolean;
  metadata?: any;
  created_at: Date;
  updated_at: Date;
}

export interface TipFilters {
  categoryId?: string;
  categorySlug?: string;
  status?: string;
  isPublic?: boolean;
  isFeatured?: boolean;
  search?: string;
  animalType?: string;
  page?: number;
  limit?: number;
}

class TipsService {
  /**
   * Obține tips-urile
   */
  async getTips(filters: TipFilters = {}): Promise<{ data: Tip[]; total: number; pagination: any }> {
    try {
      const response = await api.get(`${API_BASE_URL}/tips`, {
        params: filters
      });

      if (response.data.success) {
        return {
          data: response.data.data || [],
          total: response.data.pagination?.total || 0,
          pagination: response.data.pagination
        };
      }

      return { data: [], total: 0, pagination: {} };
    } catch (error) {
      console.error('Error fetching tips:', error);
      throw error;
    }
  }

  /**
   * Obține un tip după ID
   */
  async getTipById(id: string): Promise<Tip | null> {
    try {
      const response = await api.get(`${API_BASE_URL}/tips/${id}`);

      if (response.data.success) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Error fetching tip:', error);
      throw error;
    }
  }

  /**
   * Obține un tip după slug
   */
  async getTipBySlug(slug: string): Promise<Tip | null> {
    try {
      const response = await api.get(`${API_BASE_URL}/tips/slug/${slug}`);

      if (response.data.success) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Error fetching tip by slug:', error);
      throw error;
    }
  }

  /**
   * Creează un tip nou
   */
  async createTip(data: {
    categoryId: string;
    title: string;
    slug?: string;
    excerpt?: string;
    content: string;
    targetRoles?: string[];
    targetAnimalTypes?: string[];
    targetRegions?: string[];
    isPublic?: boolean;
    isFeatured?: boolean;
    coverImage?: string;
    images?: string[];
    tags?: string[];
    status?: string;
  }): Promise<Tip> {
    try {
      const response = await api.post(`${API_BASE_URL}/tips`, data);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to create tip');
    } catch (error: any) {
      console.error('Error creating tip:', error);
      throw new Error(error.response?.data?.error || 'Failed to create tip');
    }
  }

  /**
   * Actualizează un tip
   */
  async updateTip(id: string, data: Partial<Tip>): Promise<Tip> {
    try {
      const response = await api.put(`${API_BASE_URL}/tips/${id}`, data);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to update tip');
    } catch (error: any) {
      console.error('Error updating tip:', error);
      throw new Error(error.response?.data?.error || 'Failed to update tip');
    }
  }

  /**
   * Șterge un tip
   */
  async deleteTip(id: string): Promise<void> {
    try {
      const response = await api.delete(`${API_BASE_URL}/tips/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete tip');
      }
    } catch (error: any) {
      console.error('Error deleting tip:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete tip');
    }
  }

  /**
   * Obține toate categoriile
   */
  async getCategories(): Promise<TipCategory[]> {
    try {
      const response = await api.get(`${API_BASE_URL}/tips/categories/all`);

      if (response.data.success) {
        return response.data.data || [];
      }

      return [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Obține tips-urile dintr-o categorie
   */
  async getTipsByCategory(categoryId: string, page: number = 1, limit: number = 20): Promise<{ data: Tip[]; total: number }> {
    try {
      const response = await api.get(`${API_BASE_URL}/tips/categories/${categoryId}/tips`, {
        params: { page, limit }
      });

      if (response.data.success) {
        return {
          data: response.data.data || [],
          total: response.data.pagination?.total || 0
        };
      }

      return { data: [], total: 0 };
    } catch (error) {
      console.error('Error fetching tips by category:', error);
      throw error;
    }
  }

  /**
   * Trimite un tip pentru aprobare
   */
  async submitForReview(tipId: string): Promise<Tip> {
    try {
      const response = await api.post(`${API_BASE_URL}/tips/${tipId}/submit`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to submit tip for review');
    } catch (error: any) {
      console.error('Error submitting tip for review:', error);
      throw new Error(error.response?.data?.error || 'Failed to submit tip for review');
    }
  }

  /**
   * Aprobă un tip (Master Admin)
   */
  async approveTip(tipId: string, comments?: string): Promise<Tip> {
    try {
      const response = await api.post(`${API_BASE_URL}/tips/${tipId}/approve`, { comments });

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to approve tip');
    } catch (error: any) {
      console.error('Error approving tip:', error);
      throw new Error(error.response?.data?.error || 'Failed to approve tip');
    }
  }

  /**
   * Respinge un tip (Master Admin)
   */
  async rejectTip(tipId: string, comments: string): Promise<Tip> {
    try {
      const response = await api.post(`${API_BASE_URL}/tips/${tipId}/reject`, { comments });

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to reject tip');
    } catch (error: any) {
      console.error('Error rejecting tip:', error);
      throw new Error(error.response?.data?.error || 'Failed to reject tip');
    }
  }

  /**
   * Obține tips-urile în așteptare aprobare
   */
  async getPendingTips(page: number = 1, limit: number = 20): Promise<{ data: Tip[]; total: number }> {
    try {
      const response = await api.get(`${API_BASE_URL}/tips/pending/review`, {
        params: { page, limit }
      });

      if (response.data.success) {
        return {
          data: response.data.data || [],
          total: response.data.pagination?.total || 0
        };
      }

      return { data: [], total: 0 };
    } catch (error) {
      console.error('Error fetching pending tips:', error);
      throw error;
    }
  }

  /**
   * Incrementă contorul de vizualizări
   */
  async incrementViews(tipId: string): Promise<void> {
    try {
      await api.post(`${API_BASE_URL}/tips/${tipId}/view`);
    } catch (error) {
      console.error('Error incrementing views:', error);
      // Nu aruncăm eroare, doar logăm
    }
  }
}

export default new TipsService();

